import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { imagesFromClipboard } from './commentImages'
import { useQuery } from '@tanstack/react-query'
import { ROLE_LABELS, type Role } from '@crm/shared'
import { getMentionableUsers } from '../../../shared/api/comments.api'

/** Imperative API наружу — родитель может попросить сфокусировать textarea
 *  (например, после клика «Ответить» на конкретном комменте). */
export type MentionInputHandle = {
  focus: () => void
}

export type MentionableUser = {
  id: string
  name: string | null
  email: string
  role: string
}

type Props = {
  value: string
  onChange: (text: string, mentions: string[]) => void
  onSubmit: () => void
  requestId: string | undefined
  /** Комментарий к позиции: список «кого упомянуть» с учётом позиции — в
   *  позиции из-за рубежа в нём есть логист (нетарифка). */
  itemId?: string
  /** Свой загрузчик списка (панель комментариев логиста — адрес нетарифки).
   *  Задан — берётся он, requestId не нужен. Вместе с ним передавайте itemId:
   *  по нему различается кеш списков разных позиций. */
  loadUsers?: () => Promise<MentionableUser[]>
  placeholder?: string
  disabled?: boolean
  /** Вставка картинок из буфера (Ctrl+V скриншота) — docs/features/comment-images.md. */
  onPasteFiles?: (files: File[]) => void
  /** Enter отправляет и пустой текст — когда к комментарию приложена картинка. */
  allowEmptySubmit?: boolean
}

/**
 * Input с поддержкой @-меншенов. При наборе `@` показывается popup-список
 * пользователей, доступных к текущей заявке. Стрелки/Enter выбирают
 * пользователя — в текст вставляется `@Имя `, в локальный массив mentions
 * добавляется его id.
 *
 * Mentions хранятся отдельным state — если пользователь удалит фрагмент
 * `@Имя` из текста, мы (для простоты MVP) НЕ удаляем id из mentions.
 * На бэке id фильтруются по реальной видимости — лишние ничему не мешают.
 */
export const MentionInput = forwardRef<MentionInputHandle, Props>(function MentionInput(
  {
    value,
    onChange,
    onSubmit,
    requestId,
    itemId,
    loadUsers,
    placeholder = 'Комментарий... (Enter — отправить, Alt/Shift+Enter — новая строка)',
    disabled = false,
    onPasteFiles,
    allowEmptySubmit = false,
  },
  ref,
) {
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        const el = inputRef.current
        if (!el) return
        el.focus()
        // курсор в конец, чтобы можно было сразу печатать
        const end = el.value.length
        el.setSelectionRange(end, end)
      },
    }),
    [],
  )

  // Auto-resize: высота textarea подгоняется под содержимое (без скролла до
  // 6 строк, дальше — внутренний скролл). Считаем по scrollHeight + reset
  // height до 'auto', иначе scrollHeight будет «залипать» на старой высоте.
  useLayoutEffect(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    const max = 6 * 20 + 12 // ~6 строк × line-height + padding
    el.style.height = Math.min(el.scrollHeight, max) + 'px'
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden'
  }, [value])
  const [mentions, setMentions] = useState<string[]>([])
  const [popupOpen, setPopupOpen] = useState(false)
  const [popupQuery, setPopupQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  // Позиция, с которой начинается активный @-токен (включая сам @).
  // Нужна, чтобы при выборе из popup заменить `@вв` на `@Имя `.
  const tokenStartRef = useRef<number>(-1)

  const { data: users = [] } = useQuery({
    queryKey: ['mentionable-users', loadUsers ? 'custom' : 'request', requestId, itemId],
    queryFn: () => (loadUsers ? loadUsers() : getMentionableUsers(requestId ?? '', itemId)),
    enabled: (!!loadUsers || !!requestId) && popupOpen,
    // Свой загрузчик без itemId — ключ один на все позиции: не держим кеш,
    // чтобы в панели другой позиции не мелькнул чужой список.
    staleTime: loadUsers && !itemId ? 0 : 5 * 60_000,
  })

  const filtered = useMemo<MentionableUser[]>(() => {
    if (!popupOpen) return []
    const q = popupQuery.trim().toLowerCase()
    if (!q) return users.slice(0, 8)
    return users
      .filter((u) => {
        const name = (u.name ?? '').toLowerCase()
        const email = u.email.toLowerCase()
        return name.includes(q) || email.includes(q)
      })
      .slice(0, 8)
  }, [users, popupQuery, popupOpen])

  useEffect(() => {
    setHighlight(0)
  }, [popupQuery, popupOpen])

  const closePopup = () => {
    setPopupOpen(false)
    setPopupQuery('')
    tokenStartRef.current = -1
  }

  const updateText = (text: string) => {
    onChange(text, mentions)
    // Поиск активного @-токена слева от курсора.
    const el = inputRef.current
    if (!el) return
    const pos = el.selectionStart ?? text.length
    const left = text.slice(0, pos)
    const m = left.match(/@([^\s@]*)$/)
    if (m && m.index !== undefined) {
      tokenStartRef.current = m.index
      setPopupQuery(m[1] ?? '')
      setPopupOpen(true)
    } else {
      closePopup()
    }
  }

  const pickUser = (u: MentionableUser) => {
    const start = tokenStartRef.current
    if (start < 0) return
    const el = inputRef.current
    if (!el) return
    const before = value.slice(0, start)
    const after = value.slice(el.selectionStart ?? value.length)
    const label = u.name?.trim() || u.email.split('@')[0] || u.email
    const insert = `@${label} `
    const next = before + insert + after
    const nextMentions = mentions.includes(u.id) ? mentions : [...mentions, u.id]
    setMentions(nextMentions)
    onChange(next, nextMentions)
    closePopup()
    // Курсор после вставки ставится сразу, как новый текст окажется в поле (эффект ниже).
    // Раньше — через setTimeout: буквы, набранные до таймера, уходили в конец, а дальше
    // курсор прыгал назад, и текст перемешивался («@accountant ёт по …?Сч», 21.10.2026).
    pendingCaretRef.current = (before + insert).length
    el.focus()
  }

  // Курсор, который нужно поставить после следующего обновления текста (выбор из списка).
  const pendingCaretRef = useRef<number | null>(null)
  useLayoutEffect(() => {
    const caret = pendingCaretRef.current
    const el = inputRef.current
    if (caret == null || !el) return
    pendingCaretRef.current = null
    el.setSelectionRange(caret, caret)
  }, [value])

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (popupOpen && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlight((h) => (h + 1) % filtered.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlight((h) => (h - 1 + filtered.length) % filtered.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const u = filtered[highlight]
        if (u) pickUser(u)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        closePopup()
        return
      }
    }
    if (e.key === 'Enter') {
      // Alt/Option+Enter (Mac/Windows) и Shift+Enter — новая строка.
      // Enter без модификаторов — отправка. Если поле пустое — пусто-отправки нет.
      if (e.altKey || e.shiftKey) return
      e.preventDefault()
      if (value.trim() || allowEmptySubmit) {
        onSubmit()
        setMentions([])
      }
    }
  }

  // Сброс локальных mentions при очистке input снаружи (после успешной отправки).
  useEffect(() => {
    if (value === '') setMentions([])
  }, [value])

  // Направление выпадашки: по умолчанию вверх (исторически она над textarea),
  // но если сверху не хватает места — открываем вниз. Считаем при каждом
  // открытии popup, чтобы реагировать на скролл/изменение layout (комментарии
  // под формой позиции — там сверху мало места, ноут обрезает список).
  const [popupDirection, setPopupDirection] = useState<'up' | 'down'>('up')
  const POPUP_HEIGHT_PX = 224 // max-h-56 = 14rem = 224px
  useLayoutEffect(() => {
    if (!popupOpen) return
    const el = inputRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const spaceAbove = rect.top
    const spaceBelow = window.innerHeight - rect.bottom
    // Открываем вверх если сверху хватает или сверху больше чем снизу.
    if (spaceAbove >= POPUP_HEIGHT_PX + 8) setPopupDirection('up')
    else if (spaceBelow >= POPUP_HEIGHT_PX + 8) setPopupDirection('down')
    else setPopupDirection(spaceAbove >= spaceBelow ? 'up' : 'down')
  }, [popupOpen])

  return (
    <div className="relative flex-1">
      <textarea
        ref={inputRef}
        rows={1}
        value={value}
        onChange={(e) => updateText(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={(e) => {
          if (!onPasteFiles) return
          const files = imagesFromClipboard(e)
          if (files.length > 0) onPasteFiles(files)
        }}
        onBlur={() => setTimeout(closePopup, 150)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none leading-5"
      />
      {popupOpen && filtered.length > 0 && (
        <div
          className={`absolute left-0 w-72 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl z-50 ${
            popupDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {filtered.map((u, idx) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickUser(u)}
              className={`block w-full text-left px-3 py-1.5 text-sm transition-colors ${
                idx === highlight ? 'bg-brand-50 text-brand-800' : 'hover:bg-gray-50 text-gray-800'
              }`}
            >
              <div className="font-medium">{u.name || u.email}</div>
              <div className="text-[11px] text-gray-400">
                {ROLE_LABELS[u.role as Role] ?? u.role} · {u.email}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
})
